import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Grid, TextField } from '@material-ui/core';
import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Autocomplete, DateTime } from '~/components/Form';
import { number, t } from '~/helpers';
import {
  storeData,
  updateData,
} from '~/store/modules/afericaoAreiaSilo/actions';

const MAX_DIGITS = 4;
const MAX_FIELD_VALUE = 9999;

const FormContent = styled.div`
  .MuiFormHelperText-root.Mui-error {
    margin-top: 3px;
  }
`;

function limitIntegerDigits(value, maxDigits = MAX_DIGITS) {
  const normalized = String(value || '').replace(',', '.');
  const [integer = '', decimal] = normalized.split('.');
  const limitedInteger = integer.slice(0, maxDigits);
  return decimal !== undefined
    ? `${limitedInteger}.${decimal}`
    : limitedInteger;
}

function limitIntegerValue(value) {
  return limitIntegerDigits(value).replace(/[.,].*/, '');
}

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();
  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector(
    (state) => state.afericaoAreiaSilo
  );

  const initials = useMemo(() => (create ? data : { ...find, ...data }), [
    create,
    data,
    find,
  ]);

  const [idposto, setIdposto] = useState(initials?.idposto || null);
  const [peso, setPeso] = useState(initials?.pesoafericaoareiasilo || '');
  const [duracao, setDuracao] = useState(
    initials?.duracaoafericaoareiasilo || ''
  );
  const [siloSelecionado, setSiloSelecionado] = useState(
    initials?.estoqueAreiaVO || null
  );

  const pesoMaximoPermitido = useMemo(() => {
    const capacidade = Number(siloSelecionado?.capacidadepostotipoareia);
    return capacidade > 0
      ? Math.min(capacidade, MAX_FIELD_VALUE)
      : MAX_FIELD_VALUE;
  }, [siloSelecionado]);

  const mensagemPesoMaximo = useMemo(() => {
    const capacidade = Number(siloSelecionado?.capacidadepostotipoareia);
    return capacidade > 0 && capacidade <= MAX_FIELD_VALUE
      ? 'Peso nao pode ser superior a capacidade cadastrada para o silo.'
      : 'Peso deve ter no maximo 4 digitos.';
  }, [siloSelecionado]);

  const vazao = useMemo(() => {
    const pesoNumber = Number(peso);
    const duracaoNumber = Number(duracao);
    if (!pesoNumber || !duracaoNumber) return 0;
    return pesoNumber / duracaoNumber;
  }, [peso, duracao]);

  const siloParams = useMemo(
    () =>
      idposto
        ? {
            wherefield: ['idposto', 'silotipoareia'],
            valuewherefield: [idposto, 'S'],
            typewherefield: ['EQUALS', 'EQUALS'],
            orderfield: 'nomepostotipoareia',
            ordination: 'asc',
          }
        : {},
    [idposto]
  );

  const handlePostoValue = useCallback(
    (value) => {
      const novoPosto = value ? value.idposto : null;
      const postoAlterado = novoPosto !== idposto;
      setIdposto(novoPosto);
      reference.current.setFieldValue('idposto', novoPosto);
      if (postoAlterado) {
        reference.current.setFieldValue('idpostotipoareia', null);
        reference.current.setFieldValue('idusuarioafericaoareiasilo', null);
        setSiloSelecionado(null);
      }
    },
    [idposto, reference]
  );

  const handleSiloValue = useCallback(
    (value) => {
      setSiloSelecionado(value || null);
      reference.current.setFieldValue(
        'idpostotipoareia',
        value ? value.idpostotipoareia : null
      );
    },
    [reference]
  );

  const handlePesoInput = useCallback((event) => {
    event.target.value = limitIntegerDigits(event.target.value);
  }, []);

  const handlePesoValue = useCallback((value) => {
    setPeso(limitIntegerDigits(value));
  }, []);

  const handleDuracaoInput = useCallback((event) => {
    event.target.value = limitIntegerValue(event.target.value);
  }, []);

  const handleDuracaoValue = useCallback((value) => {
    setDuracao(limitIntegerValue(value));
  }, []);

  const handleSubmit = useCallback(
    async (param) => {
      try {
        const schema = Yup.object().shape({
          idposto: Yup.number()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio')),
          idpostotipoareia: Yup.number()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio')),
          datahoraafericaoareiasilo: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          pesoafericaoareiasilo: Yup.number()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .moreThan(0, t('s.geral.validacao.campoobrigatorio'))
            .max(pesoMaximoPermitido, mensagemPesoMaximo)
            .required(t('s.geral.validacao.campoobrigatorio')),
          duracaoafericaoareiasilo: Yup.number()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .integer(t('s.geral.validacao.campoobrigatorio'))
            .moreThan(0, t('s.geral.validacao.campoobrigatorio'))
            .max(MAX_FIELD_VALUE, 'Duracao deve ter no maximo 4 digitos.')
            .required(t('s.geral.validacao.campoobrigatorio')),
          idusuarioafericaoareiasilo: Yup.number()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio')),
        });

        await schema.validate(param, { abortEarly: false });

        const objSend = {
          ...initials,
          ...param,
          idcliente: client,
          idposto: create ? Number(param.idposto) : initials.idposto,
          idpostotipoareia: Number(param.idpostotipoareia),
          pesoafericaoareiasilo: Number(
            limitIntegerDigits(param.pesoafericaoareiasilo)
          ),
          duracaoafericaoareiasilo: Number(
            limitIntegerValue(param.duracaoafericaoareiasilo)
          ),
          vazaoafericaoareiasilo: vazao,
          idusuarioafericaoareiasilo: Number(param.idusuarioafericaoareiasilo),
        };

        if (create) {
          dispatch(storeData(objSend, params));
        } else {
          dispatch(updateData(objSend, params));
        }
      } catch (err) {
        const validationErrors = {};
        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });
          reference.current.setErrors(validationErrors);
        }
      }
    },
    [
      client,
      create,
      dispatch,
      initials,
      mensagemPesoMaximo,
      params,
      pesoMaximoPermitido,
      reference,
      vazao,
    ]
  );

  useEffect(() => {
    if (loading || !reference.current) return;
    reference.current.setData(initials);
    setIdposto(initials?.idposto || null);
    setPeso(initials?.pesoafericaoareiasilo || '');
    setDuracao(initials?.duracaoafericaoareiasilo || '');
    setSiloSelecionado(initials?.estoqueAreiaVO || null);
  }, [loading, initials, reference]);

  return loading ? (
    <div style={{ height: '14rem', width: '100%' }}>
      <Spinner loading />
    </div>
  ) : (
    <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
      <FormContent>
        <Grid
          container
          spacing={2}
          justifyContent="center"
          alignItems="flex-start"
          style={{ width: '100%', margin: 0 }}
        >
          <Grid item xs={12} sm={6}>
            <Autocomplete
              name="idposto"
              label="codigonomeposto"
              url="postos"
              unique="idposto"
              title={t('s.menu.configuracao.posto')}
              disabled={!create}
              getValue={handlePostoValue}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              key={`silo-${idposto || 'sem-posto'}`}
              name="idpostotipoareia"
              label="nomepostotipoareia"
              url="estoqueareia"
              unique="idpostotipoareia"
              title={t('s.operacao.afericaoareiasilo.silo')}
              params={siloParams}
              disabled={!idposto}
              getValue={handleSiloValue}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateTime
              name="datahoraafericaoareiasilo"
              label={t('s.operacao.afericaoareiasilo.datahora')}
              time
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              name="idusuarioafericaoareiasilo"
              label="nomeusuario"
              url="usuarios"
              unique="idusuario"
              title={t('s.geral.executante')}
              disabled={!idposto}
              params={{
                filterusrposto: idposto,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Input
              name="pesoafericaoareiasilo"
              label="Peso Kg"
              variant="outlined"
              fullWidth
              type="number"
              inputProps={{ min: 0, max: pesoMaximoPermitido, step: '0.01' }}
              InputLabelProps={{ shrink: true }}
              onInput={handlePesoInput}
              getValue={handlePesoValue}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Input
              name="duracaoafericaoareiasilo"
              label="Duração s"
              variant="outlined"
              fullWidth
              type="number"
              inputProps={{ min: 0, max: MAX_FIELD_VALUE, step: 1 }}
              InputLabelProps={{ shrink: true }}
              onInput={handleDuracaoInput}
              getValue={handleDuracaoValue}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Vazão Kg/s"
              value={number(vazao, '4')}
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Input
              name="observacaoafericaoareiasilo"
              label={t('s.geral.observacao')}
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: 255 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </FormContent>
    </Form>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.shape({
    current: PropTypes.shape({
      setData: PropTypes.func,
      setErrors: PropTypes.func,
      setFieldValue: PropTypes.func,
    }),
  }).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.shape({}),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
